import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import {merge, Subscription} from "rxjs";
import {Pending} from "../shared/decorators/pending.decorator";
import {DisposeMixin} from "../shared/mixins/dispose.mixin";
import {LockMixin} from "../shared/mixins/lock.mixin";
import {EmptyMixin} from "../shared/mixins/empty.mixin";
import {DropdownOptionModel} from "../shared/models/components/form-controls/dropdown-option.model";
import {AggregationGroupModel} from "../shared/models/search/aggregations/aggregation-group.model";
import {SearchResultsFilterModel} from "../shared/models/search/results/filters/search-results-filter.model";
import {ProductResultItemModel} from "../shared/models/search/results/items/product-result-item.model";
import {SearchResultItemModel} from "../shared/models/search/results/items/search-result-item.model";
import {SearchResultModel} from "../shared/models/search/results/search-result.model";
import {WindowRefService} from "../shared/services/window-ref.service";
import {CollectionFilterProxyComponent} from "./components/form-controls/collection-filter-proxy/collection-filter-proxy.component";
import {CollectionSearchForm} from "./forms/collection-search.form";
import {CollectionSearchParamsModel} from "./models/collection-search-params.model";
import {CollectionResultsFilter} from "./models/search-results/filters/collection-results-filter";
import {CollectionSearchConfigService} from "./services/collection-search-config.service";
import {CollectionSearchService} from "./services/collection-search.service";
import {CollectionSearchParamsFactoryService} from "./services/factories/collection-search-params-factory.service";
import {ProductsSearchModeEnum} from "../shared/enums/products-search-mode.enum";

@Component({
    selector: "pcg-ng-collection-search",
    template: require('./collection-search.component.html')
})
export class CollectionSearchComponent extends DisposeMixin(LockMixin(EmptyMixin)) implements OnInit, OnDestroy {
    private formSubscription: Subscription;

    @Pending
    private searchSubscription: Subscription;

    @ViewChild(CollectionFilterProxyComponent)
    private filterProxy: CollectionFilterProxyComponent;

    public searchForm: CollectionSearchForm;
    public searchResult: SearchResultModel<SearchResultItemModel, SearchResultsFilterModel>;
    public searchMode: ProductsSearchModeEnum = ProductsSearchModeEnum.Collection;

    public collections: AggregationGroupModel;
    public sortingOptions: Array<DropdownOptionModel> = new Array<DropdownOptionModel>();

    public constructor(
        private domElement: ElementRef,
        private formBuilder: FormBuilder,
        private searchParamsFactory: CollectionSearchParamsFactoryService,
        private searchService: CollectionSearchService,
        private configService: CollectionSearchConfigService,
        private windowRefService: WindowRefService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.configService.initialize(this.domElement.nativeElement.dataset);

        this.sortingOptions = this.configService.getSortingOptions();

        const searchParams = this.searchParamsFactory.createParamsByQueryString(WindowRefService.getQueryString());
        this.searchForm = CollectionSearchForm.createForm(this.formBuilder, searchParams, this.searchParamsFactory);
        this.formSubscription = merge(
            this.searchForm.changeObservable,
            this.searchForm.submitObservable
        )
        .subscribe((params: CollectionSearchParamsModel) => this.runSearch(params));

        this.searchForm.submit();
    }

    public ngOnDestroy(): void {
        this.dispose();
    }

    public submitForm(): void {
        this.searchForm.submit();
    }

    public clearForm(): void {
        this.searchForm.reset();
        this.searchForm.submit();
        this.filterProxy.clearFilter();
    }

    private runSearch(searchParams: CollectionSearchParamsModel): void {
        this.searchResult = null;

        this.searchSubscription = this.searchService.getSearchResult(searchParams)
            .subscribe(
                (result: SearchResultModel<ProductResultItemModel, CollectionResultsFilter>) => {
                    this.searchResult = result;
                    this.collections = result.filter.collections;

                    this.filterProxy.collection = searchParams.collection;
                    this.filterProxy.searchResult = result;
                    this.filterProxy.createCollectionFilter();
                },
                () => {
                });
    }

    public get isEmptyResult(): boolean {
        return !this.searchResult || !this.searchResult.items || !this.searchResult.items.length;
    }
}
